import DataTable, {
  type TableColumn,
  type SortOrder
} from "react-data-table-component";
import { useMemo, useCallback } from "react";
import { Typography } from "../atoms/Typography";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { Template, TemplateStatus } from "../../types/templates";
import type { TemplateSortBy } from "../../services/templatesService";
import { Download } from "lucide-react";
import { createDataTablePagination } from "./DataTablePagination";

const templatesTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30]
});

type TemplatesTableProps = {
  readonly templates: Template[];
  readonly onToggleStatus: (id: string, newStatus: TemplateStatus) => void;
  readonly onDownload: (template: Template) => void;
  readonly sortBy?: TemplateSortBy;
  readonly sortDir?: "asc" | "desc";
  readonly onSort?: (sortBy: TemplateSortBy, sortDir: "asc" | "desc") => void;
};

const StatusToggle = ({
  template,
  onToggleStatus
}: {
  readonly template: Template;
  readonly onToggleStatus: (id: string, newStatus: TemplateStatus) => void;
}) => {
  const isActive = template.status === "Active";
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={isActive}
        onClick={() =>
          onToggleStatus(template.id, isActive ? "Inactive" : "Active")
        }
        className={
          isActive
            ? "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-primary-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            : "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        }
      >
        <span
          className={
            isActive
              ? "pointer-events-none inline-block h-5 w-5 translate-x-5 rounded-full bg-white shadow-sm ring-0 transition-transform"
              : "pointer-events-none inline-block h-5 w-5 translate-x-0 rounded-full bg-white shadow-sm ring-0 transition-transform"
          }
        />
      </button>
      <span className="text-sm text-gray-700">{template.status}</span>
    </div>
  );
};

const DownloadButton = ({
  template,
  onDownload
}: {
  readonly template: Template;
  readonly onDownload: (template: Template) => void;
}) => (
  <button
    type="button"
    onClick={() => onDownload(template)}
    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
    aria-label={`Download ${template.fileName}`}
    title={`Download ${template.fileName}`}
  >
    <Download className="h-5 w-5"/>
  </button>
);

export const TemplatesTable = ({
  templates,
  onToggleStatus,
  onDownload,
  sortBy = "created_at",
  sortDir = "desc",
  onSort
}: TemplatesTableProps) => {
  const handleSort = useCallback(
    (
      selectedColumn: TableColumn<Template>,
      sortDirection: SortOrder
    ) => {
      const id =
        selectedColumn.id == null ? "created_at" : String(selectedColumn.id);
      const apiSortBy = id as TemplateSortBy;
      onSort?.(apiSortBy, sortDirection === "asc" ? "asc" : "desc");
    },
    [onSort]
  );

  const columns: TableColumn<Template>[] = useMemo(
    () => [
      {
        id: "template_name",
        name: "Document Name",
        selector: (row) => row.name,
        sortable: true,
        grow: 2,
        cell: (row) => (
          <Typography variant="body">{row.name}</Typography>
        )
      },
      {
        name: "Template Type",
        selector: (row) => row.contractCategory,
        cell: (row) => (
          <Typography variant="body">{row.contractCategory || "—"}</Typography>
        )
      },
      {
        name: "Business Units",
        selector: (row) => row.businessUnits.join(", "),
        cell: (row) => (
          <Typography variant="body">
            {row.businessUnits.length > 0
              ? row.businessUnits.join(", ")
              : "—"}
          </Typography>
        )
      },
      {
        id: "created_by",
        name: "Created By",
        selector: (row) => row.createdBy,
        sortable: true
      },
      {
        id: "created_at",
        name: "Created Date",
        selector: (row) => row.createdDate,
        sortable: true
      },
      {
        name: "Status",
        cell: (row) => (
          <StatusToggle template={row} onToggleStatus={onToggleStatus} />
        ),
        width: "180px"
      },
      {
        name: "Actions",
        cell: (row) => (
          <DownloadButton template={row} onDownload={onDownload} />
        ),
        width: "80px",
        right: true
      }
    ],
    [onToggleStatus, onDownload]
  );

  return (
    <DataTable
      key={`${sortBy}-${sortDir}`}
      columns={columns}
      data={templates}
      customStyles={dataTableCustomStyles}
      striped={false}
      pagination
      paginationPerPage={10}
      paginationRowsPerPageOptions={[10, 20, 30]}
      paginationComponent={templatesTablePagination}
      sortServer
      onSort={onSort ? handleSort : undefined}
      defaultSortFieldId={sortBy}
      defaultSortAsc={sortDir === "asc"}
      noDataComponent={
        <div className="p-12 text-center">
          <Typography variant="muted">
            No templates found. Create your first template to get started.
          </Typography>
        </div>
      }
      highlightOnHover
      responsive
    />
  );
};
